import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import axios from "axios";
import toast from "react-hot-toast";
import "../../assets/css/occasion.css";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getMainGuestsAndAttendees } from "../../utils/getGuestWithAttendees";
import {
  getGuestAccomodation,
  addAccomodation,
  updateAccomodation,
  deleteAccomodation,
  getHotelsByEventId,
  updateTravel,
  getTravelByGuest,
  getGuestJourney,
  getAllTravel,
  deleteTravel,
} from "../../server/Api";
import GuestJourneyModal from "../../components/GuestJourneyModal";
import {
  GENERAL_ROOM_TYPE,
  getHotelRoomTypeOptions,
  roomMatchesSelectedType,
  normalizeNights,
  computeHotelPricing,
} from "../../utils/hotelRoomUtils";

const TRAVEL_STAY_STEPS = [
  { id: "stay", label: "Stay", icon: "bi-house-door-fill" },
  { id: "arrival", label: "Arrival", icon: "bi-box-arrow-in-right" },
  { id: "departure", label: "Departure", icon: "bi-box-arrow-up-right" },
  { id: "local", label: "Local", icon: "bi-car-front-fill" },
];

const TRAVEL_MODES = ["Flight", "Train", "Bus", "Car", "Other"];

const travelSelectProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  classNamePrefix: "travel-select",
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  },
};

const STAY_STATUSES = [
  "Assigned",
  "Checked-in",
  "Checked-Out",
  "Cancelled",
];

/** Map legacy Pending/Confirmed/Checked-out to the 4 canonical stay statuses. */
const normalizeStayStatus = (status) => {
  const lower = String(status || "")
    .trim()
    .toLowerCase();
  if (!lower || lower === "pending" || lower === "confirmed") return "Assigned";
  if (lower === "checked-in") return "Checked-in";
  if (lower === "checked-out" || lower === "checked out") return "Checked-Out";
  if (lower === "cancelled" || lower === "canceled") return "Cancelled";
  if (lower === "assigned") return "Assigned";
  return "Assigned";
};

const toDateOnly = (value) => String(value || "").split("T")[0];

const addDaysToDate = (dateStr, days) => {
  const clean = toDateOnly(dateStr);
  if (!clean) return "";
  const parsed = new Date(`${clean}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return "";
  parsed.setDate(parsed.getDate() + Number(days || 0));
  return parsed.toISOString().slice(0, 10);
};

/** Hotel nights between check-in and check-out (checkout day not counted). */
const nightsBetweenDates = (checkIn, checkOut) => {
  const startRaw = toDateOnly(checkIn);
  const endRaw = toDateOnly(checkOut);
  if (!startRaw || !endRaw) return null;
  const start = new Date(`${startRaw}T12:00:00`);
  const end = new Date(`${endRaw}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const diff = Math.round((end - start) / (24 * 60 * 60 * 1000));
  return diff >= 1 ? diff : null;
};

const emptyStayForm = () => ({
    checkInDate: "",
    checkOutDate: "",
  totalNights: "",
  stayStatus: "Assigned",
  roomType: "",
  occupancy: "",
  hotelId: "",
  roomIds: [],
    noOfRooms: "",
  specialRequirements: "",
});

const emptyTravelForm = () => ({
    occasionId: "",
  arrivalDate: "",
  arrivalTime: "",
  arrivalMode: "",
  arrivalFlightTrainBusNo: "",
  arrivalPNRNo: "",
  arrivalPickUpLocation: "",
  arrivalBags: "",
  arrivalNoOfTags: "",
  arrivalTagName: "",
  pickupRequired: false,
  pickupLocation: "",
  pickupDate: "",
  pickupTime: "",
  pickupVehicle: "",
  pickupVehicleNumber: "",
  pickupChauffeur: "",
  pickupDriverMobile: "",
  pickupInstructions: "",
  departureDate: "",
  departureTime: "",
  departureMode: "",
  departureFlightTrainBusNo: "",
  departurePNRNo: "",
  departureDropLocation: "",
  departureBags: "",
  departureNoOfTags: "",
  departureTagName: "",
  dropRequired: false,
  dropLocation: "",
  dropDate: "",
  dropTime: "",
  dropVehicle: "",
  dropVehicleNumber: "",
  dropChauffeur: "",
  dropDriverMobile: "",
  dropInstructions: "",
  carType: "",
  carQuantity: "",
  separateCar: "",
  localVehicleType: "",
  localVehicleNumber: "",
  localChauffeur: "",
  localPickupDrop: "",
  localSharedVehicle: false,
  vipLevel: "",
  meetAndGreet: false,
  luggageAssistance: false,
  earlyCheckIn: false,
  lateCheckout: false,
  itineraryNotes: "",
});

const formatPersonName = (person = {}) =>
  `${person.prefix || ""} ${person.firstName || ""} ${
    person.MiddleName || person.middleName || ""
  } ${person.lastName || ""} ${person.suffix || ""}`
    .replace(/\s+/g, " ")
    .trim();

const getStayFingerprint = (acc) => {
  const rooms = [...(acc.roomIds || [])].map(String).sort().join(",");
  return `${acc.hotelId || ""}|${rooms}|${acc.checkInDate || ""}|${
    acc.checkOutDate || ""
  }`;
};

const getStayAnchorId = (acc) => {
  if (acc.guestModel === "AttendeeMaster" && acc.mainGuestId) {
    return String(acc.mainGuestId);
  }
  return String(acc.guestId);
};

const groupStayRecordsForDisplay = (accommodations = []) => {
  const byKey = new Map();

  accommodations.forEach((acc) => {
    const key = `${getStayAnchorId(acc)}|${getStayFingerprint(acc)}`;
    if (!byKey.has(key)) {
      byKey.set(key, { primary: acc, records: [acc] });
      return;
    }
    const group = byKey.get(key);
    group.records.push(acc);
    if (acc.guestModel === "GuestMaster") {
      group.primary = acc;
    }
  });

  return Array.from(byKey.values()).map(({ primary, records }) => {
    if (records.length === 1) {
      return {
        ...primary,
        _groupedRecordIds: [primary._id],
      };
    }

    const attendeeIdSet = new Set((primary.attendeeIds || []).map(String));
    let includesMainGuest =
      primary.includesMainGuest !== false &&
      primary.guestModel === "GuestMaster";

    records.forEach((record) => {
      if (record.guestModel === "AttendeeMaster") {
        attendeeIdSet.add(String(record.guestId));
      }
      if (record.guestModel === "GuestMaster") {
        includesMainGuest = true;
      }
      if (record.includesMainGuest === false) {
        includesMainGuest = false;
      }
      (record.attendeeIds || []).forEach((id) =>
        attendeeIdSet.add(String(id))
      );
    });

    return {
      ...primary,
      includesMainGuest,
      attendeeIds: Array.from(attendeeIdSet),
      guestId: getStayAnchorId(primary),
      guestModel: "GuestMaster",
      _groupedRecordIds: records.map((record) => record._id),
      _isLegacyGroup: true,
    };
  });
};

const getStayMainGuestIds = (acc) => {
  const fromList = (acc?.guestIds || []).map(String).filter(Boolean);
  if (fromList.length) return [...new Set(fromList)];
  const anchor = getStayAnchorId(acc);
  return anchor && anchor !== "undefined" ? [anchor] : [];
};

const getStayOccupantNames = (acc, guestsWithAttendees) => {
  const mainGuestIds = getStayMainGuestIds(acc);
  const names = [];

  const includesMain =
    acc.includesMainGuest !== false &&
    (acc.guestModel === "GuestMaster" || acc._isLegacyGroup);

  if (includesMain) {
    mainGuestIds.forEach((guestId) => {
      const bundle = guestsWithAttendees.find(
        (item) => String(item.guest._id) === String(guestId)
      );
      if (bundle?.guest) {
        names.push({
          label: formatPersonName(bundle.guest),
          type: "Guest",
        });
      }
    });
  }

  const attendeeIdList = [...(acc.attendeeIds || []).map(String)];
  if (acc.guestModel === "AttendeeMaster" && !acc.attendeeIds?.length) {
    attendeeIdList.push(String(acc.guestId));
  }

  [...new Set(attendeeIdList)].forEach((id) => {
    let attendee = null;
    for (const guestId of mainGuestIds) {
      const bundle = guestsWithAttendees.find(
        (item) => String(item.guest._id) === String(guestId)
      );
      attendee = (bundle?.attendees || []).find(
        (item) => String(item._id) === id
      );
      if (attendee) break;
    }
    if (!attendee) {
      for (const bundle of guestsWithAttendees) {
        attendee = (bundle?.attendees || []).find(
          (item) => String(item._id) === id
        );
        if (attendee) break;
      }
    }
    if (attendee) {
      names.push({
        label: formatPersonName(attendee),
        type: "Attendee",
      });
    }
  });

  if (!names.length && mainGuestIds[0]) {
    const bundle = guestsWithAttendees.find(
      (item) => String(item.guest._id) === String(mainGuestIds[0])
    );
    if (bundle?.guest) {
      names.push({
        label: formatPersonName(bundle.guest),
        type: "Guest",
      });
    }
  }

  return names;
};

const formatDisplayTime = (time) => {
  const value = String(time || "").trim();
  if (!value) return "—";
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return value;
  const hours = Number(match[1]);
  const minutes = match[2];
  const period = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes} ${period}`;
};

const travelHasArrival = (travel) =>
  Boolean(
    travel?.arrivalDate ||
      travel?.arrivalTime ||
      travel?.arrivalMode ||
      travel?.arrivalModeOfTravel ||
      travel?.arrivalPickUpLocation ||
      travel?.pickupRequired
  );

const travelHasDeparture = (travel) =>
  Boolean(
    travel?.departureDate ||
      travel?.departureTime ||
      travel?.departureMode ||
      travel?.departureModeOfTravel ||
      travel?.departureDropLocation ||
      travel?.dropRequired
  );

const travelHasLocal = (travel) =>
  Boolean(
    travel?.localVehicleType ||
      travel?.localVehicleNumber ||
      travel?.localChauffeur ||
      travel?.localPickupDrop ||
      travel?.localSharedVehicle
  );

const resolveTravelPeople = (travel, guestsWithAttendees = []) => {
  const guestId = String(travel?.guestId || "");
  const attendeeId = travel?.attendeeId ? String(travel.attendeeId) : "";
  const bundle = guestsWithAttendees.find(
    (item) => String(item.guest._id) === guestId
  );
  const mainGuestName = bundle?.guest
    ? formatPersonName(bundle.guest)
    : "—";
  let attendeeName = "";
  if (attendeeId) {
    const attendee = (bundle?.attendees || []).find(
      (item) => String(item._id) === attendeeId
    );
    attendeeName = attendee ? formatPersonName(attendee) : "—";
  }
  return {
    mainGuestName,
    attendeeName,
    displayName: attendeeName || mainGuestName,
  };
};

const filterTravelsBySection = (travels = [], section) => {
  if (section === "arrival") return travels.filter(travelHasArrival);
  if (section === "departure") return travels.filter(travelHasDeparture);
  if (section === "local") return travels.filter(travelHasLocal);
  return [];
};

const findTravelForGuest = (travels = [], guestId) =>
  travels.find((travel) => String(travel?.guestId || "") === String(guestId));

const getGuestSectionCompletion = (guestId, accommodations = [], travels = []) => {
  const hasStay = accommodations.some(
    (acc) => getStayAnchorId(acc) === String(guestId)
  );
  const travel = findTravelForGuest(travels, guestId);
  return {
    stay: hasStay,
    arrival: travelHasArrival(travel),
    departure: travelHasDeparture(travel),
    local: travelHasLocal(travel),
  };
};

const SIDEBAR_SECTION_META = {
  stay: { title: "Stay Records", empty: "No stay records found." },
  arrival: { title: "Arrival Records", empty: "No arrival records found." },
  departure: {
    title: "Departure Records",
    empty: "No departure records found.",
  },
  local: {
    title: "Local Transport Records",
    empty: "No local transport records found.",
  },
};

const getStayStatusClass = (status) => {
  const normalized = String(normalizeStayStatus(status))
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
  return `is-${normalized}`;
};

const isCheckedInStatus = (status) =>
  normalizeStayStatus(status) === "Checked-in";

const isCheckedOutStatus = (status) =>
  normalizeStayStatus(status) === "Checked-Out";

const buildStayRecordViewData = (acc, guestsWithAttendees, hotels) => {
  const occupants = getStayOccupantNames(acc, guestsWithAttendees);
  const attendees = occupants.filter((item) => item.type === "Attendee");
  const hotel = hotels.find((h) => String(h._id) === String(acc.hotelId));
  const roomLabels = (() => {
    const idSet = new Set((acc.roomIds || []).map(String));
    return (hotel?.rooms || [])
      .filter((room) => idSet.has(String(room._id)))
      .map((room) => room.roomNumber);
  })();

  const mainGuestNames = occupants
    .filter((item) => item.type === "Guest")
    .map((item) => item.label);
  const mainGuestName = mainGuestNames.length
    ? mainGuestNames.join(", ")
    : "—";

  return {
    occupants,
    mainGuestName,
    mainGuestNames,
    attendeeNames: attendees.map((item) => item.label),
    guestLabel: [...mainGuestNames, ...attendees.map((item) => item.label)]
      .filter((name) => name && name !== "—")
      .join(", "),
    stayStatus: normalizeStayStatus(acc.stayStatus),
    hotelName: hotel?.hotelName || "—",
    roomType: acc.roomType || "",
    roomNumbers: roomLabels.join(", "),
    checkInDate: acc.checkInDate
      ? new Date(acc.checkInDate).toLocaleDateString()
      : "—",
    checkOutDate: acc.checkOutDate
      ? new Date(acc.checkOutDate).toLocaleDateString()
      : "—",
    checkInTime: formatDisplayTime(
      acc.checkInTime || hotel?.standardCheckInTime || "14:00"
    ),
    checkOutTime: formatDisplayTime(
      acc.checkOutTime || hotel?.standardCheckOutTime || "11:00"
    ),
  };
};

const renderStayRecordCard = (
  acc,
  guestsWithAttendees,
  hotels,
  handlers,
  sectionCompletion = null
) => {
  const {
    mainGuestName,
    attendeeNames,
    guestLabel,
    stayStatus,
    hotelName,
    roomType,
    roomNumbers,
    checkInDate,
    checkOutDate,
    checkInTime,
    checkOutTime,
  } = buildStayRecordViewData(acc, guestsWithAttendees, hotels);

  return (
    <div
      className={`acc-detail-card acc-stay-card ${getStayStatusClass(stayStatus)}`}
      key={acc._id}
    >
      {sectionCompletion ? (
        <div className="acc-stay-section-icons" aria-label="Section completion">
          {TRAVEL_STAY_STEPS.map((step) => {
            const isComplete = Boolean(sectionCompletion[step.id]);
            return (
              <span
                key={step.id}
                className={`acc-stay-section-icon${
                  isComplete ? " is-complete" : ""
                }`}
                title={`${step.label}${isComplete ? " — saved" : " — not saved"}`}
              >
                <i
                  className={`bi ${step.icon} section-glyph`}
                  aria-hidden="true"
                />
                {isComplete ? (
                  <i
                    className="bi bi-check-circle-fill section-check"
                    aria-hidden="true"
                  />
                ) : null}
              </span>
            );
          })}
        </div>
      ) : null}

      <div className="acc-stay-party">
        <div className="acc-stay-party-block is-main-guest">
          <div className="acc-stay-party-head">
            <span className="acc-stay-role-badge is-main-guest">
              Main Guest{mainGuestName.includes(",") ? "s" : ""}
            </span>
            <span
              className={`acc-stay-status-pill ${getStayStatusClass(stayStatus)}`}
            >
              {stayStatus}
            </span>
          </div>
          {mainGuestName.includes(",") ? (
            <ul className="acc-stay-attendee-list">
              {mainGuestName.split(", ").map((name) => (
                <li key={name}>
                  <strong className="acc-stay-party-name">{name}</strong>
                </li>
              ))}
            </ul>
          ) : (
            <strong className="acc-stay-party-name">{mainGuestName}</strong>
          )}
        </div>
        {attendeeNames.length ? (
          <div className="acc-stay-party-block is-attendees">
            <div className="acc-stay-party-head">
              <span className="acc-stay-role-badge is-attendee">
                Attendee{attendeeNames.length > 1 ? "s" : ""}
              </span>
            </div>
            <ul className="acc-stay-attendee-list">
              {attendeeNames.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <div className="acc-stay-stay-meta">
        <div className="acc-stay-meta-line">
          <i className="bi bi-building" aria-hidden="true" />
          <span>{hotelName}</span>
        </div>
        {roomType || roomNumbers ? (
          <div className="acc-stay-meta-line">
            <i className="bi bi-door-open" aria-hidden="true" />
            <span>
              {roomType || "Room"}
              {roomNumbers ? ` · ${roomNumbers}` : ""}
            </span>
          </div>
        ) : null}
      </div>

      <div className="acc-stay-datetime-grid">
        <div
          className={`acc-stay-datetime-block${
            isCheckedInStatus(stayStatus) ? " is-checked-in" : ""
          }`}
        >
          <span className="acc-stay-datetime-label">Check-in</span>
          <strong className="acc-stay-datetime-date">{checkInDate}</strong>
          <span className="acc-stay-datetime-time">{checkInTime}</span>
        </div>
        <div
          className={`acc-stay-datetime-block${
            isCheckedOutStatus(stayStatus) ? " is-checked-out" : ""
          }`}
        >
          <span className="acc-stay-datetime-label">Check-out</span>
          <strong className="acc-stay-datetime-date">{checkOutDate}</strong>
          <span className="acc-stay-datetime-time">{checkOutTime}</span>
        </div>
      </div>

      <div className="acc-detail-actions">
        <button
          type="button"
          className="btn travel-stay-card-preview-btn"
          onClick={() => handlers.onPreview?.(acc)}
          title="Preview guest journey"
          aria-label="Preview guest journey"
        >
          <i className="bi bi-eye" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="btn edit-btn"
          onClick={() => handlers.onEdit(acc)}
        >
          Edit
        </button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={() =>
            handlers.onDelete({
              accommodationIds: acc._groupedRecordIds || [acc._id],
              guestName: guestLabel,
            })
          }
        >
          Delete
        </button>
      </div>
    </div>
  );
};

const renderStayExpandedTable = (
  records,
  guestsWithAttendees,
  hotels,
  handlers,
  editAccommodationId
) => (
  <div className="table-responsive stay-expanded-table-wrap">
    <table className="table table-bordered table-hover stay-expanded-table">
      <thead className="thead-dark">
        <tr>
          <th>Sn.</th>
          <th>Main Guest</th>
          <th>Attendee(s)</th>
          <th>Status</th>
          <th>Hotel</th>
          <th>Room</th>
          <th>Check-in</th>
          <th>Check-out</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {records.length > 0 ? (
          records.map((acc, index) => {
            const row = buildStayRecordViewData(
              acc,
              guestsWithAttendees,
              hotels
            );
            const editIds = (acc._groupedRecordIds || [acc._id]).map(String);
            const isEditing =
              editAccommodationId &&
              editIds.includes(String(editAccommodationId));

            return (
              <tr
                key={acc._id}
                className={`${getStayStatusClass(row.stayStatus)}${
                  isEditing ? " is-editing" : ""
                }`}
              >
                <td>{index + 1}</td>
                <td className="stay-expanded-main-guest">{row.mainGuestName}</td>
                <td className="stay-expanded-attendees">
                  {row.attendeeNames.length ? (
                    <ul className="stay-expanded-attendee-list">
                      {row.attendeeNames.map((name) => (
                        <li key={name}>{name}</li>
                      ))}
                    </ul>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  <span
                    className={`acc-stay-status-pill ${getStayStatusClass(
                      row.stayStatus
                    )}`}
                  >
                    {row.stayStatus}
                  </span>
                </td>
                <td className="stay-expanded-hotel">{row.hotelName}</td>
                <td className="stay-expanded-room">
                  {row.roomType || row.roomNumbers ? (
                    <div className="stay-expanded-room-stack">
                      <span className="stay-expanded-room-type">
                        {row.roomType || "Room"}
                      </span>
                      {row.roomNumbers ? (
                        <span className="stay-expanded-room-numbers">
                          · {row.roomNumbers}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    "—"
                  )}
                </td>
                <td
                  className={`stay-expanded-datetime is-check-in-col${
                    isCheckedInStatus(row.stayStatus) ? " is-checked-in" : ""
                  }`}
                >
                  <strong>{row.checkInDate}</strong>
                  <span>{row.checkInTime}</span>
                </td>
                <td
                  className={`stay-expanded-datetime is-check-out-col${
                    isCheckedOutStatus(row.stayStatus) ? " is-checked-out" : ""
                  }`}
                >
                  <strong>{row.checkOutDate}</strong>
                  <span>{row.checkOutTime}</span>
                </td>
                <td className="stay-table-actions">
                  <button
                    type="button"
                    className="btn travel-stay-card-preview-btn btn-sm"
                    onClick={() => handlers.onPreview?.(acc)}
                    title="Preview guest journey"
                  >
                    <i className="bi bi-eye" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="btn edit-btn btn-sm"
                    onClick={() => handlers.onEdit(acc)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() =>
                      handlers.onDelete({
                        accommodationIds: acc._groupedRecordIds || [acc._id],
                        guestName: row.guestLabel,
                      })
                    }
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan="9" className="text-center">
              No stay records found.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const renderTravelRecordCard = (travel, guestsWithAttendees, section, handlers) => {
  const people = resolveTravelPeople(travel, guestsWithAttendees);
  const sectionLabel =
    section === "arrival"
      ? "Arrival"
      : section === "departure"
        ? "Departure"
        : "Local";
  const lines = [];

  if (section === "arrival") {
    lines.push({
      icon: "bi-calendar-event",
      text: [
        travel.arrivalDate
          ? new Date(travel.arrivalDate).toLocaleDateString()
          : null,
        formatDisplayTime(travel.arrivalTime),
      ]
        .filter((part) => part && part !== "—")
        .join(" · ") || "Schedule pending",
    });
    lines.push({
      icon: "bi-signpost",
      text:
        travel.arrivalMode ||
        travel.arrivalModeOfTravel ||
        travel.arrivalPickUpLocation ||
        "—",
    });
    if (travel.pickupRequired) {
      lines.push({ icon: "bi-car-front", text: "Pickup required" });
    }
  } else if (section === "departure") {
    lines.push({
      icon: "bi-calendar-event",
      text: [
        travel.departureDate
          ? new Date(travel.departureDate).toLocaleDateString()
          : null,
        formatDisplayTime(travel.departureTime),
      ]
        .filter((part) => part && part !== "—")
        .join(" · ") || "Schedule pending",
    });
    lines.push({
      icon: "bi-signpost",
      text:
        travel.departureMode ||
        travel.departureModeOfTravel ||
        travel.departureDropLocation ||
        "—",
    });
    if (travel.dropRequired) {
      lines.push({ icon: "bi-box-arrow-right", text: "Drop-off required" });
    }
  } else {
    lines.push({
      icon: "bi-car-front",
      text:
        [travel.localVehicleType, travel.localVehicleNumber]
          .filter(Boolean)
          .join(" · ") || "Local transport",
    });
    if (travel.localChauffeur) {
      lines.push({ icon: "bi-person", text: travel.localChauffeur });
    }
    if (travel.localPickupDrop) {
      lines.push({ icon: "bi-geo-alt", text: travel.localPickupDrop });
    }
  }

  return (
    <div className="acc-detail-card acc-stay-card is-pending" key={travel._id}>
      <div className="acc-stay-party">
        <div className="acc-stay-party-block is-main-guest">
          <div className="acc-stay-party-head">
            <span className="acc-stay-role-badge is-main-guest">Main Guest</span>
            <span className="acc-stay-status-pill is-confirmed">
              {sectionLabel}
            </span>
          </div>
          <strong className="acc-stay-party-name">{people.mainGuestName}</strong>
        </div>
        {people.attendeeName ? (
          <div className="acc-stay-party-block is-attendees">
            <div className="acc-stay-party-head">
              <span className="acc-stay-role-badge is-attendee">Attendee</span>
            </div>
            <ul className="acc-stay-attendee-list">
              <li>{people.attendeeName}</li>
            </ul>
          </div>
        ) : null}
      </div>

      <div className="acc-stay-stay-meta">
        {lines.map((line) => (
          <div
            className="acc-stay-meta-line"
            key={`${travel._id}-${line.icon}-${line.text}`}
          >
            <i className={`bi ${line.icon}`} aria-hidden="true" />
            <span>{line.text}</span>
          </div>
        ))}
      </div>

      <div className="acc-detail-actions">
        <button
          type="button"
          className="btn travel-stay-card-preview-btn"
          onClick={() => handlers.onPreview?.(travel)}
          title="Preview guest journey"
          aria-label="Preview guest journey"
        >
          <i className="bi bi-eye" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="btn edit-btn"
          onClick={() => handlers.onEdit(travel)}
        >
          Edit
        </button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={() =>
            handlers.onDelete({
              travelId: travel._id,
              guestName: people.displayName,
            })
          }
        >
          Delete
        </button>
      </div>
    </div>
  );
};

const renderTravelExpandedTable = (
  records,
  guestsWithAttendees,
  section,
  handlers,
  editingTravelId
) => {
  const columns =
    section === "arrival"
      ? [
          "Date",
          "Time",
          "Mode",
          "Flight / Train / Bus",
          "PNR",
          "Location",
          "Pickup",
          "Pickup Location",
          "Vehicle",
          "Driver",
        ]
      : section === "departure"
        ? [
            "Date",
            "Time",
            "Mode",
            "Flight / Train / Bus",
            "PNR",
            "Location",
            "Drop",
            "Drop Location",
            "Vehicle",
            "Driver",
          ]
        : [
            "Vehicle Type",
            "Vehicle No.",
            "Chauffeur",
            "Pickup / Drop",
            "Shared",
          ];

  return (
    <div className="table-responsive stay-expanded-table-wrap">
      <table className="table table-bordered table-hover stay-expanded-table">
        <thead className="thead-dark">
          <tr>
            <th>Sn.</th>
            <th>Main Guest</th>
            <th>Attendee</th>
            {columns.map((col) => (
              <th key={col}>{col}</th>
            ))}
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {records.length > 0 ? (
            records.map((travel, index) => {
              const people = resolveTravelPeople(travel, guestsWithAttendees);
              const isEditing =
                editingTravelId &&
                String(editingTravelId) === String(travel._id);

              let detailCells = [];
              if (section === "arrival") {
                detailCells = [
                  travel.arrivalDate
                    ? new Date(travel.arrivalDate).toLocaleDateString()
                    : "—",
                  formatDisplayTime(travel.arrivalTime),
                  travel.arrivalMode || travel.arrivalModeOfTravel || "—",
                  travel.arrivalFlightTrainBusNo || "—",
                  travel.arrivalPNRNo || "—",
                  travel.arrivalPickUpLocation || "—",
                  travel.pickupRequired ? "Yes" : "No",
                  travel.pickupLocation || "—",
                  travel.pickupVehicle || travel.pickupVehicleNumber || "—",
                  travel.pickupChauffeur || "—",
                ];
              } else if (section === "departure") {
                detailCells = [
                  travel.departureDate
                    ? new Date(travel.departureDate).toLocaleDateString()
                    : "—",
                  formatDisplayTime(travel.departureTime),
                  travel.departureMode || travel.departureModeOfTravel || "—",
                  travel.departureFlightTrainBusNo || "—",
                  travel.departurePNRNo || "—",
                  travel.departureDropLocation || "—",
                  travel.dropRequired ? "Yes" : "No",
                  travel.dropLocation || "—",
                  travel.dropVehicle || travel.dropVehicleNumber || "—",
                  travel.dropChauffeur || "—",
                ];
              } else {
                detailCells = [
                  travel.localVehicleType || "—",
                  travel.localVehicleNumber || "—",
                  travel.localChauffeur || "—",
                  travel.localPickupDrop || "—",
                  travel.localSharedVehicle ? "Yes" : "No",
                ];
              }

              return (
                <tr
                  key={travel._id}
                  className={isEditing ? "is-editing" : ""}
                >
                  <td>{index + 1}</td>
                  <td>{people.mainGuestName}</td>
                  <td>{people.attendeeName || "—"}</td>
                  {detailCells.map((cell, cellIndex) => (
                    <td key={`${travel._id}-${cellIndex}`}>{cell}</td>
                  ))}
                  <td className="stay-table-actions">
                    <button
                      type="button"
                      className="btn travel-stay-card-preview-btn btn-sm"
                      onClick={() => handlers.onPreview?.(travel)}
                      title="Preview guest journey"
                      aria-label="Preview guest journey"
                    >
                      <i className="bi bi-eye" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="btn edit-btn btn-sm"
                      onClick={() => handlers.onEdit(travel)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() =>
                        handlers.onDelete({
                          travelId: travel._id,
                          guestName: people.displayName,
                        })
                      }
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={4 + columns.length} className="text-center">
                {SIDEBAR_SECTION_META[section]?.empty || "No records found."}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const Accomodation = () => {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState("stay");
  const [mainGuestIds, setMainGuestIds] = useState([]);
  const [attendeeIds, setAttendeeIds] = useState([]);
  const mainGuestId = mainGuestIds[0] || "";
  const setMainGuestId = (id) => {
    const next = id ? [String(id)] : [];
    setMainGuestIds(next);
  };
  const [guestsWithAttendees, setGuestsWithAttendees] = useState([]);
  const [accommodations, setAccommodations] = useState([]);
  const [travels, setTravels] = useState([]);
  const [hotels, setHotels] = useState([]);
  const location = useLocation();
  const navMainGuestId = location.state?.mainGuestId;
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);
  const eventEntity = useSelector((state) => state.auth.user);
  const [isEditingStay, setIsEditingStay] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [editAccommodationId, setEditAccommodationId] = useState(null);
  const [travelRecordId, setTravelRecordId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [stayForm, setStayForm] = useState(emptyStayForm());
  const [travelForm, setTravelForm] = useState(emptyTravelForm());
  const [journeyData, setJourneyData] = useState(null);
  const [isJourneyModalOpen, setIsJourneyModalOpen] = useState(false);
  const [sidebarListType, setSidebarListType] = useState("stay");
  const [legacyStayCleanupIds, setLegacyStayCleanupIds] = useState([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    type: "stay",
    accommodationIds: [],
    travelId: null,
    guestName: "",
  });
  const arrivalFormScrollRef = useRef(null);
  const departureFormScrollRef = useRef(null);
  const updateFormScrollRef = useRef(null);
  const staySectionRef = useRef(null);
  const arrivalSectionRef = useRef(null);
  const departureSectionRef = useRef(null);
  const localSectionRef = useRef(null);
  const pickupFlowRef = useRef(null);
  const dropFlowRef = useRef(null);
  const scrollSpyLockedRef = useRef(false);

  const scrollToFormSection = (sectionRef, scrollContainerRef) => {
    window.requestAnimationFrame(() => {
      if (!sectionRef.current) return;
      if (scrollContainerRef?.current) {
        const container = scrollContainerRef.current;
        const section = sectionRef.current;
        const top =
          section.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop -
          12;
        container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        return;
      }
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  /** Guests/attendees already on an active stay (exclude cancelled + current edit). */
  const assignedStayPeople = useMemo(() => {
    const guestIds = new Set();
    const attendeeIdSet = new Set();
    const editingIds = new Set(
      [
        editAccommodationId,
        ...(legacyStayCleanupIds || []),
      ]
        .filter(Boolean)
        .map(String)
    );

    accommodations.forEach((acc) => {
      if (normalizeStayStatus(acc.stayStatus) === "Cancelled") return;
      const recordIds = (acc._groupedRecordIds || [acc._id]).map(String);
      if (recordIds.some((id) => editingIds.has(id))) return;

      getStayMainGuestIds(acc).forEach((id) => guestIds.add(String(id)));
      (acc.attendeeIds || []).forEach((id) => attendeeIdSet.add(String(id)));
      if (acc.guestModel === "AttendeeMaster" && acc.guestId) {
        attendeeIdSet.add(String(acc.guestId));
      }
    });

    return { guestIds, attendeeIds: attendeeIdSet };
  }, [accommodations, editAccommodationId, legacyStayCleanupIds]);

  const guestOptions = guestsWithAttendees
    .filter((item) => {
      const id = String(item.guest._id);
      if (mainGuestIds.includes(id)) return true;
      return !assignedStayPeople.guestIds.has(id);
    })
    .map((item) => ({
      value: String(item.guest._id),
      label: formatPersonName(item.guest),
    }));

  const selectedGuestBundles = guestsWithAttendees.filter((item) =>
    mainGuestIds.includes(String(item.guest._id))
  );

  const attendeeOptions = selectedGuestBundles.flatMap((bundle) =>
    (bundle.attendees || [])
      .filter((att) => {
        const id = String(att._id);
        if (attendeeIds.includes(id)) return true;
        return !assignedStayPeople.attendeeIds.has(id);
      })
      .map((att) => ({
        value: String(att._id),
        label:
          mainGuestIds.length > 1
            ? `${formatPersonName(bundle.guest)} · ${formatPersonName(att)}`
            : formatPersonName(att),
        mainGuestId: String(bundle.guest._id),
      }))
  );

  const primaryAttendeeId = attendeeIds[0] || "";
  const computedOccupancy = mainGuestIds.length + attendeeIds.length;

  const getAccommodations = useCallback(async () => {
    try {
      if (!selectedEvent?.eventId) return;
      const res = await axios.get(getGuestAccomodation(selectedEvent.eventId));
      if (res.status === 200 && res.data.data) {
        setAccommodations(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching accommodations:", error);
      toast.error("Failed to fetch stay records.");
    }
  }, [selectedEvent?.eventId]);

  const getHotels = useCallback(async () => {
    try {
      if (!selectedEvent?.eventId) return;
      const res = await axios.get(getHotelsByEventId(selectedEvent.eventId));
      if (res.status === 200 && res.data.data) {
        setHotels(res.data.data);
      }
      } catch (error) {
      console.error("Error fetching hotels:", error);
    }
  }, [selectedEvent?.eventId]);

  const getTravels = useCallback(async () => {
      try {
        if (!selectedEvent?.eventId) return;
      const res = await axios.get(getAllTravel(selectedEvent.eventId));
      if (res.status === 200) {
        setTravels(res.data?.data || []);
      }
      } catch (error) {
      console.error("Error fetching travels:", error);
      toast.error("Failed to fetch travel records.");
      }
  }, [selectedEvent?.eventId]);

  const loadGuestsWithAttendees = useCallback(async () => {
    try {
      if (!selectedEvent?.eventId) return;
      const data = await getMainGuestsAndAttendees(selectedEvent.eventId);
      setGuestsWithAttendees(data || []);
    } catch (error) {
      console.error("Error fetching guests:", error);
      toast.error("Failed to fetch guests.");
    }
  }, [selectedEvent?.eventId]);

  const loadTravelRecord = useCallback(async () => {
    if (!selectedEvent?.eventId || !mainGuestId) {
      setTravelForm(emptyTravelForm());
      setTravelRecordId(null);
      return;
    }
    try {
      const res = await axios.get(
        getTravelByGuest(
          selectedEvent.eventId,
          mainGuestId,
          primaryAttendeeId || "null"
        )
      );
      const record = res.data?.data;
      if (record) {
        setTravelRecordId(record._id);
        setTravelForm({
          ...emptyTravelForm(),
          occasionId: record.occasionId || "",
          arrivalDate: record.arrivalDate?.split("T")[0] || record.arrivalDate || "",
          arrivalTime: record.arrivalTime || "",
          arrivalMode: record.arrivalMode || record.arrivalModeOfTravel || "",
          arrivalFlightTrainBusNo: record.arrivalFlightTrainBusNo || "",
          arrivalPNRNo: record.arrivalPNRNo || "",
          arrivalPickUpLocation: record.arrivalPickUpLocation || "",
          arrivalBags: record.arrivalBags || "",
          arrivalNoOfTags: record.arrivalNoOfTags || "",
          arrivalTagName: record.arrivalTagName || "",
          pickupRequired: Boolean(record.pickupRequired),
          pickupLocation: record.pickupLocation || "",
          pickupDate:
            record.pickupDate?.split("T")[0] || record.pickupDate || "",
          pickupTime: record.pickupTime || "",
          pickupVehicle: record.pickupVehicle || "",
          pickupVehicleNumber: record.pickupVehicleNumber || "",
          pickupChauffeur: record.pickupChauffeur || "",
          pickupDriverMobile: record.pickupDriverMobile || "",
          pickupInstructions: record.pickupInstructions || "",
          departureDate:
            record.departureDate?.split("T")[0] || record.departureDate || "",
          departureTime: record.departureTime || "",
          departureMode:
            record.departureMode || record.departureModeOfTravel || "",
          departureFlightTrainBusNo: record.departureFlightTrainBusNo || "",
          departurePNRNo: record.departurePNRNo || "",
          departureDropLocation: record.departureDropLocation || "",
          departureBags: record.departureBags || "",
          departureNoOfTags: record.departureNoOfTags || "",
          departureTagName: record.departureTagName || "",
          dropRequired: Boolean(record.dropRequired),
          dropLocation: record.dropLocation || "",
          dropDate: record.dropDate?.split("T")[0] || record.dropDate || "",
          dropTime: record.dropTime || "",
          dropVehicle: record.dropVehicle || "",
          dropVehicleNumber: record.dropVehicleNumber || "",
          dropChauffeur: record.dropChauffeur || "",
          dropDriverMobile: record.dropDriverMobile || "",
          dropInstructions: record.dropInstructions || "",
          carType: record.carType || "",
          carQuantity: record.carQuantity || "",
          separateCar: record.separateCar || "",
          localVehicleType: record.localVehicleType || "",
          localVehicleNumber: record.localVehicleNumber || "",
          localChauffeur: record.localChauffeur || "",
          localPickupDrop: record.localPickupDrop || "",
          localSharedVehicle: Boolean(record.localSharedVehicle),
          vipLevel: record.vipLevel || "",
          meetAndGreet: Boolean(record.meetAndGreet),
          luggageAssistance: Boolean(record.luggageAssistance),
          earlyCheckIn: Boolean(record.earlyCheckIn),
          lateCheckout: Boolean(record.lateCheckout),
          itineraryNotes: record.itineraryNotes || "",
        });
      } else {
        setTravelRecordId(null);
        setTravelForm(emptyTravelForm());
      }
    } catch (error) {
      console.error("Error fetching travel:", error);
    }
  }, [selectedEvent?.eventId, mainGuestId, primaryAttendeeId]);

  const loadJourney = useCallback(async () => {
    if (!selectedEvent?.eventId || !mainGuestId) {
      setJourneyData(null);
      return;
    }
    try {
      const res = await axios.get(
        getGuestJourney(selectedEvent.eventId, mainGuestId, primaryAttendeeId || "")
      );
      setJourneyData(res.data?.data || null);
    } catch (error) {
      console.error("Error fetching journey:", error);
    }
  }, [selectedEvent?.eventId, mainGuestId, primaryAttendeeId]);

  useEffect(() => {
    if (navMainGuestId && !isEditingStay) {
      setMainGuestIds([String(navMainGuestId)]);
    }
  }, [navMainGuestId, isEditingStay]);

  useEffect(() => {
    getAccommodations();
    getHotels();
    getTravels();
    loadGuestsWithAttendees();
  }, [getAccommodations, getHotels, getTravels, loadGuestsWithAttendees]);

  useEffect(() => {
    loadTravelRecord();
    loadJourney();
  }, [loadTravelRecord, loadJourney]);

  useEffect(() => {
    setSidebarListType(activeSection);
  }, [activeSection]);

  useEffect(() => {
    if (!isUpdateMode) return undefined;
    const container = updateFormScrollRef.current;
    if (!container) return undefined;

    const sectionEntries = [
      { id: "stay", ref: staySectionRef },
      { id: "arrival", ref: arrivalSectionRef },
      { id: "departure", ref: departureSectionRef },
      { id: "local", ref: localSectionRef },
    ];

    const syncActiveFromScroll = () => {
      if (scrollSpyLockedRef.current) return;
      const marker = container.getBoundingClientRect().top + 96;
      let current = sectionEntries[0].id;
      sectionEntries.forEach(({ id, ref }) => {
        const el = ref.current;
        if (!el) return;
        if (el.getBoundingClientRect().top <= marker) {
          current = id;
        }
      });
      setActiveSection((prev) => (prev === current ? prev : current));
    };

    container.addEventListener("scroll", syncActiveFromScroll, {
      passive: true,
    });
    syncActiveFromScroll();
    return () => {
      container.removeEventListener("scroll", syncActiveFromScroll);
    };
  }, [isUpdateMode]);

  const handleStayChange = (e) => {
    const { name, value } = e.target;
    if (name === "hotelId") {
      const hotel = hotels.find((h) => String(h._id) === String(value));
      const types = getHotelRoomTypeOptions(hotel?.rooms || []);
      const hotelNights = normalizeNights(hotel?.totalNights);
      setStayForm((prev) => {
        const nights = prev.totalNights
          ? normalizeNights(prev.totalNights)
          : hotelNights;
        const checkInDate = prev.checkInDate || "";
        const checkOutDate = checkInDate
          ? addDaysToDate(checkInDate, nights)
          : prev.checkOutDate;
        return {
          ...prev,
          hotelId: value,
          roomType: types.length === 1 ? types[0] : "",
          roomIds: [],
          noOfRooms: "",
          totalNights: String(nights),
          checkOutDate,
        };
      });
      return;
    }
    if (name === "roomType") {
      setStayForm((prev) => ({
        ...prev,
        roomType: value,
        roomIds: [],
        noOfRooms: "",
      }));
      return;
    }
    if (name === "checkInDate") {
      setStayForm((prev) => {
        const nights =
          nightsBetweenDates(value, prev.checkOutDate) ||
          normalizeNights(prev.totalNights || selectedHotel?.totalNights || 1);
        const checkOutDate = value
          ? addDaysToDate(value, nights)
          : prev.checkOutDate;
        return {
          ...prev,
          checkInDate: value,
          checkOutDate,
          totalNights: value ? String(nights) : prev.totalNights,
        };
      });
      return;
    }
    if (name === "checkOutDate") {
      setStayForm((prev) => {
        const nights = nightsBetweenDates(prev.checkInDate, value);
        return {
          ...prev,
          checkOutDate: value,
          totalNights: nights ? String(nights) : prev.totalNights,
        };
      });
      return;
    }
    if (name === "totalNights") {
      setStayForm((prev) => {
        const nights = normalizeNights(value);
        return {
          ...prev,
          totalNights: value,
          checkOutDate: prev.checkInDate
            ? addDaysToDate(prev.checkInDate, nights)
            : prev.checkOutDate,
        };
      });
      return;
    }
    setStayForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTravelChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTravelForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePickupRequiredChange = (required) => {
    setTravelForm((prev) => ({
      ...prev,
      pickupRequired: required,
      ...(required
        ? {}
        : {
            pickupLocation: "",
            pickupDate: "",
            pickupTime: "",
            pickupVehicle: "",
            pickupVehicleNumber: "",
            pickupChauffeur: "",
            pickupDriverMobile: "",
            pickupInstructions: "",
          }),
    }));
    if (required) {
      setTimeout(
        () => scrollToFormSection(pickupFlowRef, arrivalFormScrollRef),
        120
      );
    }
  };

  const handleDropRequiredChange = (required) => {
    setTravelForm((prev) => ({
      ...prev,
      dropRequired: required,
      ...(required
        ? {}
        : {
            dropLocation: "",
            dropDate: "",
            dropTime: "",
            dropVehicle: "",
            dropVehicleNumber: "",
            dropChauffeur: "",
            dropDriverMobile: "",
            dropInstructions: "",
          }),
    }));
    if (required) {
      setTimeout(
        () => scrollToFormSection(dropFlowRef, departureFormScrollRef),
        120
      );
    }
  };

  const renderTravelFormActions = (label) => (
    <div className="travel-stay-form-actions">
      <button type="submit" className="btn btn-save">
        {label}
      </button>
      <button
        type="button"
        className="btn btn-outline-secondary btn-cancel"
        onClick={() => {
          if (isUpdateMode) {
            exitUpdateMode();
            return;
          }
          loadTravelRecord();
        }}
      >
        Cancel
      </button>
    </div>
  );

  const selectedHotel = hotels.find(
    (h) => String(h._id) === String(stayForm.hotelId)
  );
  const hotelRoomTypes = getHotelRoomTypeOptions(selectedHotel?.rooms || []);

  const availableRoomOptions = (selectedHotel?.rooms || [])
    .filter((room) => {
      if (stayForm.roomIds.map(String).includes(String(room._id))) return true;
      if (!stayForm.roomType) return false;
      if (!roomMatchesSelectedType(room, stayForm.roomType)) return false;
      return room.status !== "occupied";
    })
    .sort((a, b) =>
      String(a.roomNumber).localeCompare(String(b.roomNumber), undefined, {
        numeric: true,
      })
    );

  const selectedStayRooms = (selectedHotel?.rooms || []).filter((room) =>
    stayForm.roomIds.map(String).includes(String(room._id))
  );
  const stayNightsForPricing = normalizeNights(
    stayForm.totalNights ||
      nightsBetweenDates(stayForm.checkInDate, stayForm.checkOutDate) ||
      selectedHotel?.totalNights ||
      1
  );
  const stayPricing = computeHotelPricing(
    selectedStayRooms,
    stayNightsForPricing
  );

  const handleRoomSelect = (selectedOptions) => {
    const values = (selectedOptions || []).map((opt) => opt.value);
    setStayForm((prev) => ({
      ...prev,
      roomIds: values,
      noOfRooms: values.length > 0 ? String(values.length) : prev.noOfRooms,
    }));
  };

  const getStayOccupantLabels = (acc) =>
    getStayOccupantNames(acc, guestsWithAttendees).map((item) => item.label);

  const buildStayPayload = () => ({
    eventId: selectedEvent.eventId,
    guestId: mainGuestIds[0],
    guestIds: mainGuestIds,
    guestModel: "GuestMaster",
    includesMainGuest: true,
    attendeeIds: attendeeIds,
    checkInDate: stayForm.checkInDate,
    checkOutDate: stayForm.checkOutDate,
    checkInTime: selectedHotel?.standardCheckInTime || "14:00",
    checkOutTime: selectedHotel?.standardCheckOutTime || "11:00",
    stayStatus: stayForm.stayStatus,
    roomType:
      stayForm.roomType === GENERAL_ROOM_TYPE ? "" : stayForm.roomType,
    occupancy: computedOccupancy || null,
    noOfRooms: Number(stayForm.noOfRooms) || stayForm.roomIds.length || 0,
    hotelId: stayForm.hotelId || null,
    roomIds: stayForm.roomIds || [],
    specialRequirements: stayForm.specialRequirements,
    enteredBy: eventEntity?.username || "system",
    modifiedBy: eventEntity?.username || "system",
  });

  const handleSaveStay = async (e) => {
    e.preventDefault();
    if (!selectedEvent?.eventId) {
      toast.error("Event ID missing!");
      return;
    }
    if (!mainGuestIds.length) {
      toast.error("Please select at least one main guest");
      return;
    }
    if (!stayForm.hotelId) {
      toast.error("Please select a hotel");
      return;
    }
    if (!stayForm.roomType) {
      toast.error("Please select a room type");
      return;
    }
    if (
      stayForm.checkInDate &&
      stayForm.checkOutDate &&
      !nightsBetweenDates(stayForm.checkInDate, stayForm.checkOutDate)
    ) {
      toast.error("Check-out date must be after check-in date.");
      return;
    }

    const wasEditing = Boolean(isEditingStay && editAccommodationId);

    try {
      const payload = buildStayPayload();
      if (wasEditing) {
        await axios.put(updateAccomodation(editAccommodationId), payload);
        if (legacyStayCleanupIds.length) {
          await Promise.all(
            legacyStayCleanupIds.map((id) =>
              axios.delete(deleteAccomodation(id))
            )
          );
          setLegacyStayCleanupIds([]);
        }
        toast.success("Stay updated successfully!");
      } else {
        const createRes = await axios.post(addAccomodation(), payload);
        const createdId = createRes.data?.data?._id;
        if (createdId) {
          setIsEditingStay(true);
          setEditAccommodationId(createdId);
        } else {
          setIsEditingStay(false);
          setEditAccommodationId(null);
        }
        toast.success("Stay saved successfully!");
      }

      await getAccommodations();
      await getHotels();
      await loadJourney();

      if (!wasEditing && !isUpdateMode) {
        setActiveSection("arrival");
        setSidebarListType("arrival");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to save stay details"
      );
    }
  };

  const buildTravelPayload = () => {
    const { occasionId, ...travelFields } = travelForm;
    return {
        eventId: selectedEvent.eventId,
        guestId: mainGuestId,
      attendeeId: primaryAttendeeId || null,
      ...travelFields,
      occasionId: occasionId || null,
      arrivalModeOfTravel: travelForm.arrivalMode,
      departureModeOfTravel: travelForm.departureMode,
        enteredBy: eventEntity?.username || "system",
        modifiedBy: eventEntity?.username || "system",
    };
  };

  const handleSaveTravel = async (e, options = {}) => {
    e.preventDefault();
    if (!selectedEvent?.eventId || !mainGuestId) {
      toast.error("Please select a main guest");
      return;
    }

    try {
      const payload = buildTravelPayload();
        const res = await axios.put(
        updateTravel(mainGuestId, primaryAttendeeId || "null"),
          payload
        );
      setTravelRecordId(res.data?.data?._id || travelRecordId);
      toast.success(
        travelRecordId || isUpdateMode
          ? "Travel details updated!"
          : "Travel details saved!"
      );
      await getTravels();
      await loadTravelRecord();
      await loadJourney();

      if (!isUpdateMode && options.nextSection) {
        setActiveSection(options.nextSection);
        setSidebarListType(options.nextSection);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to save travel details"
      );
    }
  };

  const applyStayRecordToForm = (record) => {
    if (!record) return;
    setIsEditingStay(true);
    setEditAccommodationId(record._id);
    setMainGuestIds(getStayMainGuestIds(record));
    setAttendeeIds((record.attendeeIds || []).map(String));
    setLegacyStayCleanupIds(
      record._isLegacyGroup
        ? (record._groupedRecordIds || []).filter((id) => id !== record._id)
        : []
    );

    const storedRoomType = record.roomType || "";
    const partyCount =
      getStayMainGuestIds(record).length +
      (record.attendeeIds || []).length;
    const checkInDate = record.checkInDate?.split("T")[0] || "";
    const checkOutDate = record.checkOutDate?.split("T")[0] || "";
    const nights =
      nightsBetweenDates(checkInDate, checkOutDate) ||
      normalizeNights(
        hotels.find((h) => String(h._id) === String(record.hotelId))
          ?.totalNights || 1
      );
    setStayForm({
      checkInDate,
      checkOutDate,
      totalNights: String(nights),
      stayStatus: normalizeStayStatus(record.stayStatus),
      roomType: storedRoomType || GENERAL_ROOM_TYPE,
      occupancy: partyCount || record.occupancy || "",
      hotelId: record.hotelId ? String(record.hotelId) : "",
      roomIds: (record.roomIds || []).map(String),
      noOfRooms: record.noOfRooms || "",
      specialRequirements: record.specialRequirements || "",
    });
  };

  const handleEditStay = (acc) => {
    const grouped = groupStayRecordsForDisplay(accommodations).find((item) =>
      (item._groupedRecordIds || [item._id]).includes(acc._id)
    );
    const record = grouped || acc;

    applyStayRecordToForm(record);
    setIsUpdateMode(true);
    setIsSidebarExpanded(false);
    setActiveSection("stay");
    setSidebarListType("stay");
    scrollSpyLockedRef.current = true;
    setTimeout(() => {
      scrollToFormSection(staySectionRef, updateFormScrollRef);
      window.setTimeout(() => {
        scrollSpyLockedRef.current = false;
      }, 550);
    }, 80);
  };

  const handlePreviewStay = (acc) => {
    const guests = getStayMainGuestIds(acc);
    const attendees = (acc.attendeeIds || []).map(String);
    setMainGuestIds(guests);
    setAttendeeIds(attendees);
    setIsJourneyModalOpen(true);
  };

  const handlePreviewTravel = (travel) => {
    setMainGuestIds(
      travel.guestId ? [String(travel.guestId)] : []
    );
    setAttendeeIds(travel.attendeeId ? [String(travel.attendeeId)] : []);
    setIsJourneyModalOpen(true);
  };

  const handleEditTravel = (travel, section) => {
    const guestId = String(travel.guestId || "");
    const targetSection = section || "arrival";
    setMainGuestIds(guestId ? [guestId] : []);
    setAttendeeIds(travel.attendeeId ? [String(travel.attendeeId)] : []);
    setTravelRecordId(travel._id);
    setIsUpdateMode(true);
    setIsSidebarExpanded(false);

    const stayRecord = groupStayRecordsForDisplay(accommodations).find(
      (item) => getStayAnchorId(item) === guestId
    );
    if (stayRecord) {
      applyStayRecordToForm(stayRecord);
    }

    setActiveSection(targetSection);
    setSidebarListType(targetSection);
    scrollSpyLockedRef.current = true;
    setTimeout(() => {
      const refMap = {
        stay: staySectionRef,
        arrival: arrivalSectionRef,
        departure: departureSectionRef,
        local: localSectionRef,
      };
      scrollToFormSection(refMap[targetSection], updateFormScrollRef);
      window.setTimeout(() => {
        scrollSpyLockedRef.current = false;
      }, 550);
    }, 80);
  };

  const handleDeleteStay = async () => {
    if (deleteConfirmation.type === "travel") {
      if (!deleteConfirmation.travelId) return;
      try {
        await axios.delete(deleteTravel(deleteConfirmation.travelId));
        toast.success("Travel record deleted!");
        await getTravels();
        await loadTravelRecord();
        await loadJourney();
    } catch (error) {
        toast.error("Failed to delete travel record.");
    } finally {
      setDeleteConfirmation({
        isOpen: false,
          type: "stay",
          accommodationIds: [],
          travelId: null,
          guestName: "",
        });
      }
      return;
    }

    const ids = deleteConfirmation.accommodationIds || [];
    if (!ids.length) return;
    try {
      await Promise.all(ids.map((id) => axios.delete(deleteAccomodation(id))));
      toast.success("Stay record deleted!");
      await getAccommodations();
      await getHotels();
      await loadJourney();
    } catch (error) {
      toast.error("Failed to delete stay record.");
    } finally {
      setDeleteConfirmation({
        isOpen: false,
        type: "stay",
        accommodationIds: [],
        travelId: null,
        guestName: "",
      });
    }
  };

  const resetStayForm = () => {
    setStayForm(emptyStayForm());
    setIsEditingStay(false);
    setEditAccommodationId(null);
    setLegacyStayCleanupIds([]);
  };

  const exitUpdateMode = () => {
    setIsUpdateMode(false);
    resetStayForm();
    setTravelForm(emptyTravelForm());
    setTravelRecordId(null);
    setActiveSection("stay");
    setSidebarListType("stay");
    if (mainGuestId) {
      loadTravelRecord();
    }
  };

  const groupedStayRecords = groupStayRecordsForDisplay(accommodations);

  const filteredStayRecords = groupedStayRecords.filter((acc) => {
    const labels = getStayOccupantLabels(acc);
    return labels.some((name) =>
      name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const sidebarSection = isSidebarExpanded ? sidebarListType : activeSection;

  const filteredTravelRecords = filterTravelsBySection(
    travels,
    sidebarSection
  ).filter((travel) => {
    if (!searchTerm.trim()) return true;
    const people = resolveTravelPeople(travel, guestsWithAttendees);
    const haystack = `${people.mainGuestName} ${people.attendeeName}`.toLowerCase();
    return haystack.includes(searchTerm.toLowerCase());
  });

  const sidebarMeta = SIDEBAR_SECTION_META[sidebarSection] || SIDEBAR_SECTION_META.stay;

  const openStayDelete = (payload) =>
    setDeleteConfirmation({
      isOpen: true,
      type: "stay",
      accommodationIds: payload.accommodationIds || [],
      travelId: null,
      guestName: payload.guestName || "",
    });

  const openTravelDelete = (payload) =>
    setDeleteConfirmation({
      isOpen: true,
      type: "travel",
      accommodationIds: [],
      travelId: payload.travelId || null,
      guestName: payload.guestName || "",
    });

  const handleSidebarTypeSelect = (sectionId) => {
    setSidebarListType(sectionId);
    setActiveSection(sectionId);
  };

  const renderGuestSelectors = () => (
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label">Main Guest(s)</label>
        <Select
          {...travelSelectProps}
          isMulti
          options={guestOptions}
          value={guestOptions.filter((o) => mainGuestIds.includes(o.value))}
          onChange={(opts) => {
            const nextIds = (opts || []).map((o) => String(o.value));
            setMainGuestIds(nextIds);
            setAttendeeIds((prev) =>
              prev.filter((attId) =>
                guestsWithAttendees.some(
                  (bundle) =>
                    nextIds.includes(String(bundle.guest._id)) &&
                    (bundle.attendees || []).some(
                      (att) => String(att._id) === String(attId)
                    )
                )
              )
            );
            // Clearing all guests starts a fresh stay draft
            if (!nextIds.length && !isEditingStay) {
              resetStayForm();
            }
          }}
          placeholder="Select unassigned main guest(s)"
          closeMenuOnSelect={false}
          noOptionsMessage={() =>
            assignedStayPeople.guestIds.size
              ? "All remaining guests already have a stay assigned"
              : "No guests found"
          }
              />
            </div>
      <div className="col-md-6">
        <label className="form-label">Attendees (optional)</label>
        <Select
          {...travelSelectProps}
          isMulti
          options={attendeeOptions}
          value={attendeeOptions.filter((o) => attendeeIds.includes(o.value))}
          onChange={(opts) => {
            setAttendeeIds((opts || []).map((o) => String(o.value)));
          }}
          placeholder={
            mainGuestIds.length
              ? "Select unassigned attendee(s), if any"
              : "Select main guest(s) first"
          }
          isDisabled={!mainGuestIds.length}
          closeMenuOnSelect={false}
          noOptionsMessage={() =>
            "No unassigned attendees for the selected guest(s)"
          }
        />
          </div>
    </div>
  );

  const renderTravelStayStepNav = () => (
    <div
      className="travel-stay-step-nav"
      role="tablist"
      aria-label="Travel and stay sections"
    >
      {TRAVEL_STAY_STEPS.map((step, index) => (
        <React.Fragment key={step.id}>
          <button
            type="button"
            role="tab"
            aria-selected={activeSection === step.id}
            title={step.label}
            className={`travel-stay-step-pill is-${step.id}${
              activeSection === step.id ? " is-active" : ""
            }`}
            onClick={() => {
              setActiveSection(step.id);
              if (isUpdateMode) {
                scrollSpyLockedRef.current = true;
                const refMap = {
                  stay: staySectionRef,
                  arrival: arrivalSectionRef,
                  departure: departureSectionRef,
                  local: localSectionRef,
                };
                scrollToFormSection(refMap[step.id], updateFormScrollRef);
                window.setTimeout(() => {
                  scrollSpyLockedRef.current = false;
                }, 550);
              }
            }}
          >
            <i className={`bi ${step.icon}`} aria-hidden="true" />
            <span>{step.label}</span>
          </button>
          {index < TRAVEL_STAY_STEPS.length - 1 ? (
            <span className="travel-stay-step-sep" aria-hidden="true">
              <i className="bi bi-chevron-right" />
            </span>
          ) : null}
        </React.Fragment>
      ))}

      <button
        type="button"
        className="btn travel-stay-journey-preview-btn"
        onClick={() => setIsJourneyModalOpen(true)}
        disabled={!mainGuestId}
        title={
          mainGuestId
            ? "Preview guest journey"
            : "Select a guest to preview journey"
        }
        aria-label="Preview guest journey"
      >
        <i className="bi bi-eye" aria-hidden="true" />
      </button>
    </div>
  );

  const renderStaySection = ({ embedded = false } = {}) => (
    <form
      ref={staySectionRef}
      onSubmit={handleSaveStay}
      className="travel-stay-form-shell section-theme-stay"
      id="travel-section-stay"
    >
      <div
        className={
          embedded
            ? "travel-stay-form-embedded py-2"
            : "all-page-scroll travel-stay-form-scroll py-2"
        }
      >
        <div className="travel-stay-form-block">
          <h5 className="event-inner-hed">Guest</h5>
                  <hr />
          {renderGuestSelectors()}
        </div>

        <div className="travel-stay-form-block">
          <h5 className="event-inner-hed mt-3">Hotel & Room</h5>
          <hr />
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Hotel</label>
                          <select
                            className="form-select"
                name="hotelId"
                value={stayForm.hotelId}
                onChange={handleStayChange}
              >
                <option value="">Select Hotel</option>
                {hotels.map((hotel) => (
                  <option key={hotel._id} value={String(hotel._id)}>
                    {hotel.hotelName}
                    {hotel.metrics ? ` · ${hotel.metrics.available} available` : ""}
                              </option>
                            ))}
                          </select>
                        </div>
            <div className="col-md-6">
              <label className="form-label">Room Type</label>
              <select
                className="form-select"
                name="roomType"
                value={stayForm.roomType}
                onChange={handleStayChange}
                disabled={!stayForm.hotelId}
              >
                <option value="">
                  {stayForm.hotelId
                    ? hotelRoomTypes.length
                      ? "Select Room Type"
                      : "No room types configured"
                    : "Select hotel first"}
                </option>
                {hotelRoomTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Room Number(s)</label>
                          <Select
                {...travelSelectProps}
                isMulti
                isDisabled={!stayForm.hotelId || !stayForm.roomType}
                options={availableRoomOptions.map((room) => ({
                  value: room._id,
                  label: room.roomNumber,
                }))}
                value={(selectedHotel?.rooms || [])
                  .filter((room) =>
                    stayForm.roomIds.map(String).includes(String(room._id))
                  )
                  .map((room) => ({
                    value: room._id,
                    label: room.roomNumber,
                  }))}
                onChange={handleRoomSelect}
                placeholder={
                  !stayForm.hotelId
                    ? "Select hotel first"
                    : !stayForm.roomType
                      ? "Select room type first"
                      : availableRoomOptions.length
                        ? "Select room number(s)"
                        : "No available rooms for this type"
                }
                          />
                        </div>
            <div className="col-md-6">
              <label className="form-label">Occupancy</label>
              <div
                className={`travel-stay-occupancy${
                  computedOccupancy > 0 ? " has-value" : ""
                }`}
              >
                <div className="travel-stay-occupancy-count">
                  {computedOccupancy > 0 ? computedOccupancy : "—"}
                </div>
                <div className="travel-stay-occupancy-detail">
                  {computedOccupancy > 0 ? (
                    <>
                      <span>
                        {mainGuestIds.length} guest
                        {mainGuestIds.length === 1 ? "" : "s"}
                      </span>
                      {attendeeIds.length > 0 ? (
                        <span>
                          {" "}
                          + {attendeeIds.length} attendee
                          {attendeeIds.length === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className="travel-stay-occupancy-placeholder">
                      Select guests / attendees
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="travel-stay-form-block">
          <h5 className="event-inner-hed mt-3">Stay Dates & Status</h5>
          <hr />
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Stay Status</label>
              <select
                className="form-select"
                name="stayStatus"
                value={stayForm.stayStatus}
                onChange={handleStayChange}
              >
                {STAY_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Check-in Date</label>
                          <input
                            type="date"
                            name="checkInDate"
                            className="form-control"
                value={stayForm.checkInDate}
                onChange={handleStayChange}
                          />
                        </div>
            <div className="col-md-3">
              <label className="form-label">Check-out Date</label>
                          <input
                            type="date"
                            name="checkOutDate"
                            className="form-control"
                value={stayForm.checkOutDate}
                onChange={handleStayChange}
                min={stayForm.checkInDate || undefined}
                          />
                        </div>
            <div className="col-md-3">
              <label className="form-label">Total Nights</label>
                          <input
                            type="number"
                min="1"
                step="1"
                name="totalNights"
                            className="form-control"
                value={stayForm.totalNights}
                onChange={handleStayChange}
                onBlur={() =>
                  setStayForm((prev) => ({
                    ...prev,
                    totalNights: String(normalizeNights(prev.totalNights)),
                    checkOutDate: prev.checkInDate
                      ? addDaysToDate(
                          prev.checkInDate,
                          normalizeNights(prev.totalNights)
                        )
                      : prev.checkOutDate,
                  }))
                }
                placeholder={
                  selectedHotel?.totalNights
                    ? `Hotel default: ${normalizeNights(
                        selectedHotel.totalNights
                      )}`
                    : "e.g. 3"
                }
              />
            </div>
          </div>
          {selectedHotel ? (
            <div className="travel-stay-hotel-times">
              <div className="travel-stay-time-chip">
                <em>Check-in time</em>
                <strong>
                  {formatDisplayTime(
                    selectedHotel.standardCheckInTime || "14:00"
                  )}
                </strong>
              </div>
              <div className="travel-stay-time-chip">
                <em>Check-out time</em>
                <strong>
                  {formatDisplayTime(
                    selectedHotel.standardCheckOutTime || "11:00"
                  )}
                </strong>
              </div>
              {selectedStayRooms.length > 0 ? (
                <div className="travel-stay-time-chip is-pricing">
                  <em>Stay price</em>
                  <strong className="travel-stay-price-amount">
                    {stayPricing.label || "—"}
                  </strong>
                  <span className="travel-stay-price-meta">
                    {stayPricing.breakdown ||
                      `${stayNightsForPricing} night${
                        stayNightsForPricing === 1 ? "" : "s"
                      }`}
                  </span>
                </div>
              ) : (
                <div className="travel-stay-time-chip is-pricing">
                  <em>Stay price</em>
                  <strong className="travel-stay-price-amount is-muted">
                    Select room(s)
                  </strong>
                  <span className="travel-stay-price-meta">
                    {stayNightsForPricing} night
                    {stayNightsForPricing === 1 ? "" : "s"}
                  </span>
                </div>
              )}
            </div>
          ) : null}
                        </div>

        <div className="travel-stay-form-block">
          <h5 className="event-inner-hed mt-3">Notes</h5>
          <hr />
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label">Special Requirements</label>
              <textarea
                name="specialRequirements"
                            className="form-control"
                rows="2"
                value={stayForm.specialRequirements}
                onChange={handleStayChange}
                placeholder="Early check-in, adjoining rooms, accessibility..."
              />
            </div>
          </div>
        </div>
                        </div>

      <div className="travel-stay-form-actions">
        <button type="submit" className="btn btn-save">
          {isEditingStay || isUpdateMode ? "Update Stay" : "Save Stay"}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-cancel"
          onClick={() => {
            if (isUpdateMode) {
              exitUpdateMode();
              return;
            }
            resetStayForm();
          }}
        >
          Cancel
        </button>
                        </div>
    </form>
  );

  const renderArrivalSection = ({ embedded = false } = {}) => (
    <form
      ref={arrivalSectionRef}
      onSubmit={(e) =>
        handleSaveTravel(e, { nextSection: "departure" })
      }
      className="travel-stay-form-shell section-theme-arrival"
      id="travel-section-arrival"
    >
      <div
        ref={arrivalFormScrollRef}
        className={
          embedded
            ? "travel-stay-form-embedded py-2"
            : "all-page-scroll travel-stay-form-scroll py-2"
        }
      >
        <div className="travel-stay-form-block">
          <h5 className="event-inner-hed">Arrival</h5>
          <hr />
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Arrival Date</label>
              <input
                type="date"
                name="arrivalDate"
                className="form-control"
                value={travelForm.arrivalDate}
                onChange={handleTravelChange}
              />
                        </div>
            <div className="col-md-4">
              <label className="form-label">Arrival Time</label>
              <input
                type="time"
                name="arrivalTime"
                className="form-control"
                value={travelForm.arrivalTime}
                onChange={handleTravelChange}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Mode of Travel</label>
                          <select
                name="arrivalMode"
                            className="form-select"
                value={travelForm.arrivalMode}
                onChange={handleTravelChange}
              >
                <option value="">Select</option>
                {TRAVEL_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                              </option>
                            ))}
                          </select>
                        </div>
            <div className="col-md-6">
              <label className="form-label">Flight / Train / Bus No.</label>
                          <input
                type="text"
                name="arrivalFlightTrainBusNo"
                            className="form-control"
                value={travelForm.arrivalFlightTrainBusNo}
                onChange={handleTravelChange}
                          />
                        </div>
            <div className="col-md-6">
              <label className="form-label">PNR / Booking Reference</label>
                          <input
                            type="text"
                name="arrivalPNRNo"
                            className="form-control"
                value={travelForm.arrivalPNRNo}
                onChange={handleTravelChange}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Arrival Location</label>
              <input
                type="text"
                name="arrivalPickUpLocation"
                className="form-control"
                value={travelForm.arrivalPickUpLocation}
                onChange={handleTravelChange}
                placeholder="Airport, station, or arrival point"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label d-block">Pickup Required?</label>
              <div className="d-flex gap-4 pt-1">
                <div className="form-check">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="pickupRequiredYes"
                    name="pickupRequiredChoice"
                    checked={travelForm.pickupRequired}
                    onChange={() => handlePickupRequiredChange(true)}
                  />
                  <label className="form-check-label" htmlFor="pickupRequiredYes">
                    Yes
                  </label>
                </div>
                <div className="form-check">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="pickupRequiredNo"
                    name="pickupRequiredChoice"
                    checked={!travelForm.pickupRequired}
                    onChange={() => handlePickupRequiredChange(false)}
                  />
                  <label className="form-check-label" htmlFor="pickupRequiredNo">
                    No
                  </label>
                </div>
              </div>
            </div>
                        </div>
                      </div>

        {travelForm.pickupRequired ? (
          <div className="travel-stay-form-block" ref={pickupFlowRef}>
            <h5 className="event-inner-hed mt-3">Pickup Flow</h5>
                      <hr />
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Pickup Location</label>
                <input
                  type="text"
                  name="pickupLocation"
                  className="form-control"
                  value={travelForm.pickupLocation}
                  onChange={handleTravelChange}
                  placeholder="Where should the guest be picked up?"
                />
                      </div>
              <div className="col-md-3">
                <label className="form-label">Pickup Date</label>
                <input
                  type="date"
                  name="pickupDate"
                  className="form-control"
                  value={travelForm.pickupDate}
                  onChange={handleTravelChange}
                />
                  </div>
              <div className="col-md-3">
                <label className="form-label">Pickup Time</label>
                <input
                  type="time"
                  name="pickupTime"
                  className="form-control"
                  value={travelForm.pickupTime}
                  onChange={handleTravelChange}
                />
                </div>
              <div className="col-md-4">
                <label className="form-label">Pickup Vehicle</label>
                <input
                  type="text"
                  name="pickupVehicle"
                  className="form-control"
                  value={travelForm.pickupVehicle}
                  onChange={handleTravelChange}
                  placeholder="SUV, Sedan, Tempo..."
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Vehicle Number</label>
                <input
                  type="text"
                  name="pickupVehicleNumber"
                  className="form-control"
                  value={travelForm.pickupVehicleNumber}
                  onChange={handleTravelChange}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Pickup Chauffeur / Driver</label>
                <input
                  type="text"
                  name="pickupChauffeur"
                  className="form-control"
                  value={travelForm.pickupChauffeur}
                  onChange={handleTravelChange}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Driver Mobile Number</label>
                <input
                  type="text"
                  name="pickupDriverMobile"
                  className="form-control"
                  value={travelForm.pickupDriverMobile}
                  onChange={handleTravelChange}
                />
              </div>
              <div className="col-md-8">
                <label className="form-label">Pickup Instructions / Notes</label>
                <textarea
                  name="pickupInstructions"
                  className="form-control"
                  rows="2"
                  value={travelForm.pickupInstructions}
                  onChange={handleTravelChange}
                  placeholder="Terminal, gate, placard, special instructions..."
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {renderTravelFormActions(
        isUpdateMode ? "Update Arrival" : "Save Arrival"
      )}
    </form>
  );

  const renderDepartureSection = ({ embedded = false } = {}) => (
    <form
      ref={departureSectionRef}
      onSubmit={(e) => handleSaveTravel(e, { nextSection: "local" })}
      className="travel-stay-form-shell section-theme-departure"
      id="travel-section-departure"
    >
      <div
        ref={departureFormScrollRef}
        className={
          embedded
            ? "travel-stay-form-embedded py-2"
            : "all-page-scroll travel-stay-form-scroll py-2"
        }
      >
        <div className="travel-stay-form-block">
          <h5 className="event-inner-hed">Departure</h5>
                  <hr />
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Departure Date</label>
              <input
                type="date"
                name="departureDate"
                className="form-control"
                value={travelForm.departureDate}
                onChange={handleTravelChange}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Departure Time</label>
              <input
                type="time"
                name="departureTime"
                className="form-control"
                value={travelForm.departureTime}
                onChange={handleTravelChange}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Mode of Travel</label>
                    <select
                name="departureMode"
                      className="form-select"
                value={travelForm.departureMode}
                onChange={handleTravelChange}
              >
                <option value="">Select</option>
                {TRAVEL_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                        </option>
                      ))}
                    </select>
                  </div>
            <div className="col-md-6">
              <label className="form-label">Flight / Train / Bus No.</label>
              <input
                type="text"
                name="departureFlightTrainBusNo"
                className="form-control"
                value={travelForm.departureFlightTrainBusNo}
                onChange={handleTravelChange}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">PNR / Booking Reference</label>
              <input
                type="text"
                name="departurePNRNo"
                className="form-control"
                value={travelForm.departurePNRNo}
                onChange={handleTravelChange}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Departure Location</label>
              <input
                type="text"
                name="departureDropLocation"
                className="form-control"
                value={travelForm.departureDropLocation}
                onChange={handleTravelChange}
                placeholder="Airport, station, or departure point"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label d-block">Drop Required?</label>
              <div className="d-flex gap-4 pt-1">
                <div className="form-check">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="dropRequiredYes"
                    name="dropRequiredChoice"
                    checked={travelForm.dropRequired}
                    onChange={() => handleDropRequiredChange(true)}
                  />
                  <label className="form-check-label" htmlFor="dropRequiredYes">
                    Yes
                  </label>
                </div>
                <div className="form-check">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="dropRequiredNo"
                    name="dropRequiredChoice"
                    checked={!travelForm.dropRequired}
                    onChange={() => handleDropRequiredChange(false)}
                  />
                  <label className="form-check-label" htmlFor="dropRequiredNo">
                    No
                  </label>
                </div>
              </div>
            </div>
          </div>
                        </div>

        {travelForm.dropRequired ? (
          <div className="travel-stay-form-block" ref={dropFlowRef}>
            <h5 className="event-inner-hed mt-3">Drop Flow</h5>
                  <hr />
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Drop Location</label>
                          <input
                  type="text"
                  name="dropLocation"
                            className="form-control"
                  value={travelForm.dropLocation}
                  onChange={handleTravelChange}
                  placeholder="Hotel, venue, or pickup point for drop"
                          />
                        </div>
              <div className="col-md-3">
                <label className="form-label">Drop Date</label>
                <input
                  type="date"
                  name="dropDate"
                  className="form-control"
                  value={travelForm.dropDate}
                  onChange={handleTravelChange}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Drop Time</label>
                <input
                  type="time"
                  name="dropTime"
                  className="form-control"
                  value={travelForm.dropTime}
                  onChange={handleTravelChange}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Drop Vehicle</label>
                <input
                  type="text"
                  name="dropVehicle"
                  className="form-control"
                  value={travelForm.dropVehicle}
                  onChange={handleTravelChange}
                  placeholder="SUV, Sedan, Tempo..."
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Vehicle Number</label>
                <input
                  type="text"
                  name="dropVehicleNumber"
                  className="form-control"
                  value={travelForm.dropVehicleNumber}
                  onChange={handleTravelChange}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Drop Chauffeur / Driver</label>
                <input
                  type="text"
                  name="dropChauffeur"
                  className="form-control"
                  value={travelForm.dropChauffeur}
                  onChange={handleTravelChange}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Driver Mobile Number</label>
                <input
                  type="text"
                  name="dropDriverMobile"
                  className="form-control"
                  value={travelForm.dropDriverMobile}
                  onChange={handleTravelChange}
                />
              </div>
              <div className="col-md-8">
                <label className="form-label">Drop Instructions / Notes</label>
                <textarea
                  name="dropInstructions"
                  className="form-control"
                  rows="2"
                  value={travelForm.dropInstructions}
                  onChange={handleTravelChange}
                  placeholder="Terminal, gate, luggage, special instructions..."
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {renderTravelFormActions(
        isUpdateMode ? "Update Departure" : "Save Departure"
      )}
    </form>
  );

  const renderLocalSection = ({ embedded = false } = {}) => (
    <form
      ref={localSectionRef}
      onSubmit={(e) => handleSaveTravel(e)}
      className="travel-stay-form-shell section-theme-local"
      id="travel-section-local"
    >
      <div
        className={
          embedded
            ? "travel-stay-form-embedded py-2"
            : "all-page-scroll travel-stay-form-scroll py-2"
        }
      >
        <div className="travel-stay-form-block">
          <h5 className="event-inner-hed">Local Transport</h5>
          <hr />
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Vehicle Type</label>
                          <input
                            type="text"
                name="localVehicleType"
                            className="form-control"
                value={travelForm.localVehicleType}
                onChange={handleTravelChange}
              />
                        </div>
            <div className="col-md-4">
              <label className="form-label">Vehicle Number</label>
              <input
                type="text"
                name="localVehicleNumber"
                className="form-control"
                value={travelForm.localVehicleNumber}
                onChange={handleTravelChange}
              />
                      </div>
            <div className="col-md-4">
              <label className="form-label">Chauffeur / Driver</label>
              <input
                type="text"
                name="localChauffeur"
                className="form-control"
                value={travelForm.localChauffeur}
                onChange={handleTravelChange}
              />
            </div>
            <div className="col-md-8">
              <label className="form-label">Pickup / Drop Notes</label>
              <input
                type="text"
                name="localPickupDrop"
                className="form-control"
                value={travelForm.localPickupDrop}
                onChange={handleTravelChange}
              />
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="localSharedVehicle"
                  name="localSharedVehicle"
                  checked={travelForm.localSharedVehicle}
                  onChange={handleTravelChange}
                />
                <label className="form-check-label" htmlFor="localSharedVehicle">
                  Shared Vehicle
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderTravelFormActions(
        isUpdateMode ? "Update Local Transport" : "Save Local Transport"
      )}
    </form>
  );

                            return (
    <>
      <div className="container-fluid accommodation-page travel-stay-page">
        <div
          className={`occasion-top occasion-top-with-sidebar row g-4 align-items-center${
            isSidebarExpanded ? " expanded-sidebar" : ""
          }`}
        >
          <div className="col-lg-9 travel-stay-page-title-row">
            <h2 className="mb-0">Travel & Stay</h2>
          </div>
          <div className="col-lg-3 occasion-top-search-col">
            <input
              type="text"
              className="form-control mb-3"
              placeholder="Search guest..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
                </div>
              </div>

        <div className="Occasion_Master">
          <div className={`row ${isSidebarExpanded ? "expanded-sidebar" : ""}`}>
            <div className="col-lg-9" id="formColumn">
              <div className="form-section">
                {renderTravelStayStepNav()}
                {isUpdateMode ? (
                  <div
                    ref={updateFormScrollRef}
                    className="all-page-scroll travel-stay-form-scroll travel-stay-update-scroll py-2"
                  >
                    {renderStaySection({ embedded: true })}
                    {renderArrivalSection({ embedded: true })}
                    {renderDepartureSection({ embedded: true })}
                    {renderLocalSection({ embedded: true })}
                              </div>
                ) : (
                  <>
                    {activeSection === "stay" && renderStaySection()}
                    {activeSection === "arrival" && renderArrivalSection()}
                    {activeSection === "departure" && renderDepartureSection()}
                    {activeSection === "local" && renderLocalSection()}
                  </>
                        )}
                      </div>
                  </div>

              <div className="col-lg-3" id="sidebarColumn">
                <div className="event-sidebar">
                <div className="guest-list-toolbar">
                  <h6 className="mb-0 event-inner-hed">{sidebarMeta.title}</h6>
                    <button
                    type="button"
                    className="guest-sidebar-toggle"
                    onClick={() => setIsSidebarExpanded((prev) => !prev)}
                    title={isSidebarExpanded ? "Collapse list" : "Expand list"}
                  >
                    <i
                      className={`bi ${
                        isSidebarExpanded
                          ? "bi-chevron-right"
                          : "bi-chevron-left"
                      }`}
                    />
                    </button>
                              </div>
                {isSidebarExpanded ? (
                  <div
                    className="travel-stay-sidebar-type-nav"
                    role="tablist"
                    aria-label="Record type"
                  >
                    {TRAVEL_STAY_STEPS.map((step) => (
                      <button
                        key={step.id}
                        type="button"
                        role="tab"
                        aria-selected={sidebarListType === step.id}
                        className={`travel-stay-sidebar-type-pill is-${step.id}${
                          sidebarListType === step.id ? " is-active" : ""
                        }`}
                        onClick={() => handleSidebarTypeSelect(step.id)}
                      >
                        <i className={`bi ${step.icon}`} aria-hidden="true" />
                        <span>{step.label}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
                  <hr />
                  <div className="all-page-right-scroll accomodation-list">
                  {sidebarSection === "stay" ? (
                    isSidebarExpanded ? (
                      renderStayExpandedTable(
                        filteredStayRecords,
                        guestsWithAttendees,
                        hotels,
                        {
                          onPreview: handlePreviewStay,
                          onEdit: handleEditStay,
                          onDelete: openStayDelete,
                        },
                        editAccommodationId
                      )
                    ) : (
                      <div className="acc-detail-list">
                        {filteredStayRecords.length > 0 ? (
                          filteredStayRecords.map((acc) =>
                            renderStayRecordCard(
                              acc,
                              guestsWithAttendees,
                              hotels,
                              {
                                onPreview: handlePreviewStay,
                                onEdit: handleEditStay,
                                onDelete: openStayDelete,
                              },
                              getGuestSectionCompletion(
                                getStayAnchorId(acc),
                                accommodations,
                                travels
                              )
                            )
                          )
                        ) : (
                          <p>{sidebarMeta.empty}</p>
                        )}
                      </div>
                    )
                  ) : isSidebarExpanded ? (
                    renderTravelExpandedTable(
                      filteredTravelRecords,
                      guestsWithAttendees,
                      sidebarSection,
                      {
                        onPreview: handlePreviewTravel,
                        onEdit: (travel) =>
                          handleEditTravel(travel, sidebarSection),
                        onDelete: openTravelDelete,
                      },
                      travelRecordId
                    )
                  ) : (
                    <div className="acc-detail-list">
                      {filteredTravelRecords.length > 0 ? (
                        filteredTravelRecords.map((travel) =>
                          renderTravelRecordCard(
                            travel,
                            guestsWithAttendees,
                            sidebarSection,
                            {
                              onPreview: handlePreviewTravel,
                              onEdit: (record) =>
                                handleEditTravel(record, sidebarSection),
                              onDelete: openTravelDelete,
                            }
                          )
                        )
                      ) : (
                        <p>{sidebarMeta.empty}</p>
                    )}
                  </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>

          {deleteConfirmation.isOpen && (
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Confirm Delete</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() =>
                        setDeleteConfirmation({
                          isOpen: false,
                      type: "stay",
                      accommodationIds: [],
                      travelId: null,
                          guestName: "",
                        })
                      }
                />
                  </div>
                  <div className="modal-body">
                Delete {deleteConfirmation.type === "travel" ? "travel" : "stay"}{" "}
                record for <strong>{deleteConfirmation.guestName}</strong>?
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                  className="btn btn-secondary"
                      onClick={() =>
                        setDeleteConfirmation({
                          isOpen: false,
                      type: "stay",
                      accommodationIds: [],
                      travelId: null,
                          guestName: "",
                        })
                      }
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                  onClick={handleDeleteStay}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

      <GuestJourneyModal
        isOpen={isJourneyModalOpen}
        onClose={() => setIsJourneyModalOpen(false)}
        journeyData={journeyData}
        guestSelected={Boolean(mainGuestId)}
        eventName={selectedEvent?.eventName || ""}
      />
    </>
  );
};

export default Accomodation;
